import { React, useState, useContext, useEffect } from "react";
import { Button, Flex, Text, Heading, Modal, Transfer, Input } from "@cisco/spaces-dash-lib-styleguide";
import { I18nContext } from "../../../contexts/i18nContext";
import useLoader from "../../../hooks/useLoader";
import useNotification from "../../../hooks/useNotification";
import * as BusinessHierarchyService from "../../../service/business-hierarchy-service";
import "./modify-access-points.scss";

function ModifyAccessPoints(props) {
  const i18n = useContext(I18nContext);
  const { showModifyAccessPoint, cancelModifyAccessPoint, selectedNode } = props;
  const [node] = useState(selectedNode);
  const [locationId] = useState(selectedNode.data.id);
  const [zoneName] = useState(selectedNode.data.name);
  const [transferTreeSourceData, setTransferTreeSourceData] = useState({});
  const [transferTreeTargetData, setTransferTreeTargetData] = useState({});
  const [testLoad, setTestLoad] = useState(true);
  const [transferTreeSourceSet, setTransferTreeSourceSet] = useState(() => new Set());
  const [transferTreeTargetSet, setTransferTreeTargetSet] = useState(() => new Set());
  const { setLoading } = useLoader();
  const { setToastMessage } = useNotification();
  const [treeState, setTreeState] = useState("STALE");
  const [usecase, setUseCase] = useState("ADD_ZONE_FLOORS_LIST");

  let zoneData;
  let targetFloorAps = {};
  let apListByFloors = {};
  const srcDataSet = new Set();
  const targetDataSet = new Set();
  let existingTargetsAvailable = {};
  let existingSourceAvailable = {};
  let selectedKeysFromSource = new Set();
  let selectedKeysFromTarget = new Set();
  let floorList = [];
  let apList = [];
  let globalIndex = 0;

  /*
  let test_data_1 = {
    "addZone": {
      "floors": [
        {
          "name": "Main Floor",
          "aps": [
            {
              "macAddress": "48:8b:0a:35:eb:00",
              "name": "AP_04_Dev_Regression",
              "selected": true
            },
            {
              "macAddress": "48:8b:0a:37:d5:20",
              "name": "AP_01_Dev_Regression",
              "selected": false
            },
            {
              "macAddress": "48:8b:0a:35:e5:00",
              "name": "AP_03_Dev_Regression",
              "selected": false
            }
          ]
        },
        {
          "name": "Second Floor",
          "aps": [
            {
              "macAddress": "42:8b:0a:35:eb:00",
              "name": "AP_02_Dev_Regression",
              "selected": true
            },
            {
              "macAddress": "42:8b:0a:37:d5:20",
              "name": "AP_022_Dev_Regression",
              "selected": false
            },
          ]
        },
        {
          "name": "Third Floor",
          "aps": [
            {
              "macAddress": "32:8b:0a:35:eb:00",
              "name": "AP_332_Dev_Regression",
              "selected": true
            },
            {
              "macAddress": "49:8b:0a:37:d5:20",
              "name": "AP_3322_Dev_Regression",
              "selected": false
            },
          ]
        }
      ],
      "aps": [
        {
          "macAddress": "15:16:9d:29:5d:c0",
          "name": "indep-ORA6-02-AP03",
          "selected": true
        },
        {
          "macAddress": "12:16:9d:29:5d:c0",
          "name": "indep-ORA7-02-AP04",
          "selected": false
        },
        {
          "macAddress": "13:16:9d:29:5d:c0",
          "name": "indep-ORA8-02-AP04",
          "selected": false
        }
      ]
    }
  };

  let test_data_2 = {
    "floor": {
      "name": "F2",
      "id": "8b8fc22f-12a6-4b98-a747-70fd02df2c35",
      "aps": [
        {
          "macAddress": "15:16:9d:29:5d:c0",
          "name": "ORA6-02-AP03",
          "selected": true
        },
        {
          "macAddress": "17:16:9d:29:5d:c0",
          "name": "ORA7-02-AP03",
          "selected": false
        }
      ]
    },
    "type": "floor"
  }; */

  const populateResponseWithAps = (fzoneData) => {
    apList = [];
    if (fzoneData.aps) {
      apList = zoneData.aps;
    }
    if (zoneData.addZone && zoneData.addZone.aps) {
      apList = zoneData.addZone.aps;
    }
    const tempApListByFloors = {};
    const tempTargetFloorAps = {};
    if (apList.length > 0) {
      globalIndex += 1;
      apList.forEach((ap, apIndex) => {
        globalIndex += apIndex;
        const apKey = `key_parent_${globalIndex}`;
        if (!ap.selected) {
          tempApListByFloors[apKey] = {
            label: ap.name ? `${ap.name}${ap.macAddress ? ` (${ap.macAddress})` : ""}` : `(${ap.macAddress})`,
            name: ap.name,
            mac: ap.macAddress,
            isIndependent: true
          };
        } else {
          tempTargetFloorAps[apKey] = {
            label: ap.name ? `${ap.name}${ap.macAddress ? ` (${ap.macAddress})` : ""}` : `(${ap.macAddress})`,
            name: ap.name,
            mac: ap.macAddress,
            isIndependent: true
          };
        }
      });
      apListByFloors = {
        ...apListByFloors,
        ...tempApListByFloors
      };
      targetFloorAps = {
        ...targetFloorAps,
        ...tempTargetFloorAps
      };
    }
  };

  const populateTargetFloorAps = (ap, floor, floorIndex, floorApIndex, tempTargetFloorAps) => {
    const floorKey = `key_parent_${floorIndex}`;
    const apKey = `key_parent_${floorIndex}:child_${floorApIndex}`;

    // Add keys to the dataset
    targetDataSet.add(floorKey);
    targetDataSet.add(apKey);

    // Create a formatted AP label
    const apLabel = ap.name ? `${ap.name}${ap.macAddress ? ` (${ap.macAddress})` : ""}` : `(${ap.macAddress})`;

    // Create a copy of tempTargetFloorAps to avoid mutating the original
    const updatedTempTargetFloorAps = { ...tempTargetFloorAps };

    if (updatedTempTargetFloorAps[floorKey]) {
      updatedTempTargetFloorAps[floorKey].values = {
        ...updatedTempTargetFloorAps[floorKey].values,
        [apKey]: {
          label: apLabel,
          name: ap.name,
          mac: ap.macAddress,
          isIndependent: false
        }
      };
    } else {
      updatedTempTargetFloorAps[floorKey] = {
        label: floor.name,
        values: {
          [apKey]: {
            label: apLabel,
            name: ap.name,
            mac: ap.macAddress,
            isIndependent: false
          }
        }
      };
      // Optionally add floor ID if it exists
      if (floor.id) {
        updatedTempTargetFloorAps[floorKey].id = floor.id;
      }
    }

    // Merge updatedTempTargetFloorAps into targetFloorAps
    targetFloorAps = { ...targetFloorAps, ...updatedTempTargetFloorAps };
  };

  const emptyValuesRemoval = (fapListByFloors) => {
    const updatedFapList = { ...fapListByFloors }; // Clone to avoid mutating the original object

    Object.entries(updatedFapList).forEach(([key, value]) => {
      if (value.values && Object.keys(value.values).length === 0) {
        delete updatedFapList[key].values;

        // Additional check: if present in target, remove from source
        if (Object.hasOwn(targetFloorAps, key)) {
          delete updatedFapList[key];
        }
      }
    });

    return updatedFapList;
  };

  const iterateFloorAps = (floorAps, floor, floorIndex) => {
    const tempTargetFloorAps = {};
    return floorAps.reduce((acc, ap, floorApIndex) => {
      const apKey = `key_parent_${floorIndex}:child_${floorApIndex}`;
      if (ap.selected) {
        populateTargetFloorAps(ap, floor, floorIndex, floorApIndex, tempTargetFloorAps);
      } else {
        srcDataSet.add(apKey);
        acc[apKey] = {
          label: ap.name ? `${ap.name}${ap.macAddress ? ` (${ap.macAddress})` : ""}` : `(${ap.macAddress})`,
          name: ap.name,
          mac: ap.macAddress,
          isIndependent: false
        };
      }

      return acc;
    }, {});
  };

  const keyChangeEvent = (keys, side) => {
    console.log(`keys ${keys} side ${side}`);

    // Create a Set from the keys array directly
    const keysSet = new Set(keys);

    // Update the appropriate state based on the side
    if (side === "source") {
      setTransferTreeSourceSet(keysSet);
    } else if (side === "target") {
      setTransferTreeTargetSet(keysSet);
    }
  };

  const transferChangeEvent = (action, ...keys) => {
    console.log(action);
    console.log(keys);
    console.log(keys.length);
    setTestLoad(false);
    if (action == "target") {
      /* Transfer from source to target selected */

      existingTargetsAvailable = transferTreeTargetData;
      existingSourceAvailable = transferTreeSourceData;
      selectedKeysFromSource = transferTreeSourceSet;
      selectedKeysFromSource.forEach((key) => {
        if (key.indexOf(":") > -1) {
          /* Tree having Parent and Child nodes */

          const splittedKeys = key.split(":");
          if (splittedKeys[0] in existingTargetsAvailable) {
            /* Target already has parent node in it */

            const sourceParentSelected = existingSourceAvailable[splittedKeys[0]];
            const sourceChildSelected = sourceParentSelected.values[key];
            const targetKey = splittedKeys[0];

            // Check if the targetKey exists in existingTargetsAvailable
            if (targetKey in existingTargetsAvailable) {
              // Update values in existingTargetsAvailable
              existingTargetsAvailable[targetKey].values[key] = sourceChildSelected;

              // Delete the key from existingSourceAvailable if it exists
              if (targetKey in existingSourceAvailable) {
                delete existingSourceAvailable[targetKey].values[key];

                // Remove the entry from existingSourceAvailable if no values remain
                if (Object.keys(existingSourceAvailable[targetKey].values).length === 0) {
                  delete existingSourceAvailable[targetKey];
                }
              }
            }
          } else {
            /* Target doesn't has parent node in it */

            const sourceParentSelected = existingSourceAvailable[splittedKeys[0]];
            const sourceChildSelected = sourceParentSelected.values[key];
            existingTargetsAvailable[splittedKeys[0]] = {};
            existingTargetsAvailable[splittedKeys[0]].label = sourceParentSelected.label;
            if (sourceParentSelected.id) {
              existingTargetsAvailable[splittedKeys[0]].id = sourceParentSelected.id;
            }
            if (sourceParentSelected.name) {
              existingTargetsAvailable[splittedKeys[0]].name = sourceParentSelected.name;
            }
            if (sourceParentSelected.mac) {
              existingTargetsAvailable[splittedKeys[0]].mac = sourceParentSelected.mac;
            }
            if (sourceParentSelected.isIndependent) {
              existingTargetsAvailable[splittedKeys[0]].isIndependent = sourceParentSelected.isIndependent;
            }
            existingTargetsAvailable[splittedKeys[0]].values = {};
            existingTargetsAvailable[splittedKeys[0]].values[key] = sourceChildSelected;
            delete existingSourceAvailable[splittedKeys[0]].values[key];
            if (!Object.keys(existingSourceAvailable[splittedKeys[0]].values).length > 0) {
              delete existingSourceAvailable[splittedKeys[0]];
            }
          }
        } else {
          /* Tree having only Parent */
          const splittedKeys = key;
          if (splittedKeys in existingTargetsAvailable) {
            console.log(splittedKeys);
          } else {
            const sourceParentSelected = existingSourceAvailable[splittedKeys];
            existingTargetsAvailable[splittedKeys] = {};
            existingTargetsAvailable[splittedKeys].label = sourceParentSelected.label;
            if (sourceParentSelected.id) {
              existingTargetsAvailable[splittedKeys].id = sourceParentSelected.id;
            }
            if (sourceParentSelected.name) {
              existingTargetsAvailable[splittedKeys].name = sourceParentSelected.name;
            }
            if (sourceParentSelected.mac) {
              existingTargetsAvailable[splittedKeys].mac = sourceParentSelected.mac;
            }
            if (sourceParentSelected.isIndependent) {
              existingTargetsAvailable[splittedKeys].isIndependent = sourceParentSelected.isIndependent;
            }
            delete existingSourceAvailable[splittedKeys];
          }
        }
      });
      setTransferTreeSourceSet(() => new Set());
      setTransferTreeTargetSet(() => new Set());
      setTransferTreeSourceData(existingSourceAvailable);
      setTransferTreeTargetData(existingTargetsAvailable);
    }
    if (action == "source") {
      /* Transfer from target to source selected */

      existingTargetsAvailable = transferTreeTargetData;
      existingSourceAvailable = transferTreeSourceData;
      selectedKeysFromTarget = transferTreeTargetSet;
      selectedKeysFromTarget.forEach((key) => {
        if (key.indexOf(":") > -1) {
          /* Tree having Parent and Child nodes */

          const splittedKeys = key.split(":");
          if (splittedKeys[0] in existingSourceAvailable) {
            /* Source already has parent node in it */

            const targetParentSelected = existingTargetsAvailable[splittedKeys[0]];
            const targetChildSelected = targetParentSelected.values[key];
            const sourceKey = splittedKeys[0];

            // Check if the sourceKey exists in existingSourceAvailable
            if (sourceKey in existingSourceAvailable) {
              // Update values in existingSourceAvailable
              existingSourceAvailable[sourceKey].values[key] = targetChildSelected;

              // Check if the sourceKey exists in existingTargetsAvailable
              if (sourceKey in existingTargetsAvailable) {
                // Delete the key from existingTargetsAvailable
                delete existingTargetsAvailable[sourceKey].values[key];

                // Remove the entry from existingTargetsAvailable if no values remain
                if (Object.keys(existingTargetsAvailable[sourceKey].values).length === 0) {
                  delete existingTargetsAvailable[sourceKey];
                }
              }
            }
          } else {
            /* Source doesn't has parent node in it */

            const targetParentSelected = existingTargetsAvailable[splittedKeys[0]];
            const targetChildSelected = targetParentSelected.values[key];
            existingSourceAvailable[splittedKeys[0]] = {};
            existingSourceAvailable[splittedKeys[0]].label = targetParentSelected.label;
            if (targetParentSelected.id) {
              existingSourceAvailable[splittedKeys[0]].id = targetParentSelected.id;
            }
            if (targetParentSelected.name) {
              existingSourceAvailable[splittedKeys[0]].name = targetParentSelected.name;
            }
            if (targetParentSelected.mac) {
              existingSourceAvailable[splittedKeys[0]].mac = targetParentSelected.mac;
            }
            if (targetParentSelected.isIndependent) {
              existingSourceAvailable[splittedKeys[0]].isIndependent = targetParentSelected.isIndependent;
            }
            existingSourceAvailable[splittedKeys[0]].values = {};
            existingSourceAvailable[splittedKeys[0]].values[key] = targetChildSelected;
            delete existingTargetsAvailable[splittedKeys[0]].values[key];
            if (!Object.keys(existingTargetsAvailable[splittedKeys[0]].values).length > 0) {
              delete existingTargetsAvailable[splittedKeys[0]];
            }
          }
        } else {
          /* Tree having only Parent */
          const splittedKeys = key;
          if (splittedKeys in existingSourceAvailable) {
            console.log(splittedKeys);
          } else {
            const targetParentSelected = existingTargetsAvailable[splittedKeys];
            existingSourceAvailable[splittedKeys] = {};
            existingSourceAvailable[splittedKeys].label = targetParentSelected.label;
            if (targetParentSelected.id) {
              existingSourceAvailable[splittedKeys].id = targetParentSelected.id;
            }
            if (targetParentSelected.name) {
              existingSourceAvailable[splittedKeys].name = targetParentSelected.name;
            }
            if (targetParentSelected.mac) {
              existingSourceAvailable[splittedKeys].mac = targetParentSelected.mac;
            }
            if (targetParentSelected.isIndependent) {
              existingSourceAvailable[splittedKeys].isIndependent = targetParentSelected.isIndependent;
            }
            delete existingTargetsAvailable[splittedKeys];
          }
        }
      });
      setTransferTreeSourceSet(() => new Set());
      setTransferTreeTargetSet(() => new Set());
      setTransferTreeSourceData(existingSourceAvailable);
      setTransferTreeTargetData(existingTargetsAvailable);
    }
    setTreeState("UPDATED");
  };

  const getFloorApList = (floorAps) => {
    const fapList = Object.values(floorAps).map((ap) => ({
      name: ap.name,
      macAddress: ap.mac,
      selected: true
    }));

    return fapList;
  };

  const getFloorAp = (floorAp, fapList) => {
    fapList.push({
      name: floorAp.name,
      macAddress: floorAp.mac,
      selected: true
    });
  };

  const populateResponse = (fzoneData) => {
    const { addZone } = fzoneData;
    if (addZone?.floors?.length) {
      const tempApListByFloors = addZone.floors.reduce((acc, floor, floorIndex) => {
        globalIndex = floorIndex;
        // const sanitizedFloorName = floor.name.trim().replace(/\s+/g, "_");
        const floorKey = `key_parent_${floorIndex}`;
        srcDataSet.add(floorKey);

        acc[floorKey] = {
          label: floor.name,
          ...(floor.aps?.length && {
            values: iterateFloorAps(floor.aps, floor, floorIndex)
          })
        };
        return acc;
      }, {});
      apListByFloors = {
        ...apListByFloors,
        ...tempApListByFloors
      };
    }
  };

  const populateResponseWithFloorObject = (fzoneData) => {
    floorList = [];
    floorList.push(fzoneData.floor);

    if (floorList.length > 0) {
      const tempApListByFloors = floorList.reduce((acc, floor, floorIndex) => {
        globalIndex = floorIndex;
        // const sanitizedFloorName = floor.name.trim().replace(/\s+/g, "_");
        const floorKey = `key_parent_${floorIndex}`;
        srcDataSet.add(floorKey);

        acc[floorKey] = {
          label: floor.name,
          id: floor.id,
          ...(floor.aps?.length > 0 && {
            values: iterateFloorAps(floor.aps, floor, floorIndex)
          })
        };
        return acc;
      }, {});
      apListByFloors = {
        ...apListByFloors,
        ...tempApListByFloors
      };
    }
  };

  const getAvailableAPs = () => {
    setLoading(true);
    BusinessHierarchyService.getAvailableAPs(locationId)
      .then((response) => {
        if (response.data) {
          setTransferTreeSourceSet(() => new Set());
          setTransferTreeTargetSet(() => new Set());
          zoneData = response.data;
          setTestLoad(true);
          if (zoneData.addZone && zoneData.addZone.floors && zoneData.addZone.floors.length > 0) {
            /* case 1: floorlist on  addZone */
            setUseCase("ADD_ZONE_FLOORS_LIST");
            populateResponse(zoneData);
          } else if (zoneData.floor) {
            /* case 2: only one floor with apList */
            setUseCase("ADD_ZONE_SINGLE_FLOOR");
            populateResponseWithFloorObject(zoneData);
          }
          if (zoneData.aps || (zoneData.addZone && zoneData.addZone.aps)) {
            populateResponseWithAps(zoneData);
          }
          apListByFloors = emptyValuesRemoval(apListByFloors);
          console.log("populateResponseWithFloorObject", apListByFloors);

          if (Object.keys(apListByFloors).length == 0 && Object.keys(targetFloorAps).length == 0) {
            setToastMessage(i18n.t("ap-zone.no-access-points-available"), 5000, "warning");
          }

          setTransferTreeSourceData(apListByFloors);
          setTransferTreeTargetData(targetFloorAps);
        } else if (!response.status) {
          setToastMessage(response.message, 5000, "negative");
        }
        setLoading(false);
      })
      .catch((error) => {
        setLoading(false);
        setToastMessage(i18n.t("ap-zone.error-on-fetching-accesspoints"), 5000, "negative");
        console.log(`Modify Access Points: Error on getAvailableAPs${error}`);
      });
  };

  const saveAccessPointChanges = () => {
    setLoading(true);
    if (usecase == "ADD_ZONE_FLOORS_LIST" || usecase == "ADD_ZONE_SINGLE_FLOOR") {
      const ffloorList = [];
      const fapList = [];
      const selectedFloorAps = transferTreeTargetData;
      Object.values(selectedFloorAps).forEach((floorItr) => {
        if (floorItr.isIndependent) {
          getFloorAp(floorItr, fapList);
        } else {
          const floorObj = {
            name: floorItr.label,
            aps: getFloorApList(floorItr.values || {})
          };

          if (floorItr.id) {
            floorObj.id = floorItr.id;
          }

          ffloorList.push(floorObj);
        }
      });
      console.log(ffloorList);
      console.log(fapList);
      console.log(locationId);
      console.log(zoneName);
      const locationObject = {};
      locationObject.type = "zone";
      locationObject.parentLocationId = locationId;
      locationObject.floors = ffloorList;
      if (fapList && fapList.length > 0) {
        locationObject.aps = fapList;
      }
      setTransferTreeSourceSet(() => new Set());
      setTransferTreeTargetSet(() => new Set());
      BusinessHierarchyService.editApZoneCreateLocation(locationObject)
        .then((response) => {
          if (response.status) {
            setLoading(false);
            setToastMessage(i18n.t("ap-zone.successfully-modified"), 5000, "positive");
            cancelModifyAccessPoint();
          } else {
            setLoading(false);
            setToastMessage(i18n.t("ap-zone.failed-to-add-modify-access-points-msg"), 5000, "negative");
          }
          getAvailableAPs();
        })
        .catch((error) => {
          setLoading(false);
          setToastMessage(i18n.t("ap-zone.failed-to-add-modify-access-points-msg"), 5000, "negative");
          console.log(`Error on saveAccessPointChanges : ${error}`);
        });
    }
  };

  useEffect(() => {
    console.log("ModifyAccessPoints Mount");
    if (node && node.data && node.data.id) {
      getAvailableAPs();
    }

    return () => {
      console.log("ModifyAccessPoints Destroy");
    };
  }, []);

  useEffect(() => {
    console.log("TreeModified");
    console.log(existingTargetsAvailable);
    if (treeState == "UPDATED") {
      setTestLoad(true);
      setTreeState("STALE");
    }
  }, [testLoad, treeState]);

  return (
    <Modal size="xl" isOpen={showModifyAccessPoint}>
      <Flex direction="vertical" gap={24}>
        <Heading id="access-point-title">{i18n.t("ap-zone.modify-access-points-text")}</Heading>
        <Modal.Content>
          <Flex direction="vertical" justify="center" style={{ margin: "32px 0" }}>
            <Flex direction="vertical" justify="center" maxWidth={500}>
              <Input type="text" size="md" id="zone-name" label={i18n.t("ap-zone.zone-name-text")} value={zoneName} disabled />
            </Flex>
            <Flex direction="vertical" justify="center" style={{ margin: "30px 0" }}>
              <Text size="p3" weight="semi-bold">
                {i18n.t("ap-zone.select-access-points-text")}
              </Text>
            </Flex>
            {testLoad && (
              <Flex justify="center">
                {testLoad && (
                  <Transfer.Tree
                    onTransfer={transferChangeEvent}
                    onKeysChange={keyChangeEvent}
                    sourceData={transferTreeSourceData}
                    sourceKeys={transferTreeSourceSet}
                    targetData={transferTreeTargetData}
                    targetKeys={transferTreeTargetSet}
                  />
                )}
              </Flex>
            )}
          </Flex>
        </Modal.Content>
        <Flex justify="flex-end" align="center">
          <Button.Group>
            <Button kind="primary" disabled={Object.keys(transferTreeTargetData).length == 0} onClick={saveAccessPointChanges}>
              {i18n.t("add-edit-controller.add-text")}
            </Button>
            <Button kind="tertiary" onClick={cancelModifyAccessPoint}>
              {i18n.t("business-location-details.cancel-text")}
            </Button>
          </Button.Group>
        </Flex>
      </Flex>
    </Modal>
  );
}

export default ModifyAccessPoints;
